/**
 * Modal shown when the user clicks Verify on a batch that has soft warnings
 * (and zero hard blocks). Lists every active warning with full detail; the
 * user must explicitly "Verify anyway" or "Cancel".
 *
 * The acknowledged warning list is what gets sent to the verify endpoint so
 * the server can confirm the user saw the same warnings the server currently
 * sees (if they diverged, the server returns 412 and the client refetches).
 */
import { useEffect } from 'react';
import type { VerifyBlocker } from '../api';
import { Button } from '../ui/primitives';
import { Modal } from '../ui/Modal';
import { kindLabel } from './blockerChips';

export function VerifyConfirmationModal({
  warnings,
  onConfirm,
  onCancel,
  busy,
}: {
  warnings: VerifyBlocker[];
  onConfirm: () => void;
  onCancel: () => void;
  busy: boolean;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel, busy]);

  return (
    <Modal onClose={onCancel} size="sm">
      <div className="p-5">
        <h2 id="verify-warnings-title" className="text-base font-semibold text-ink">
          Verify with {warnings.length} warning{warnings.length === 1 ? '' : 's'}?
        </h2>
        <p className="mt-2 text-small text-ink-muted">
          These issues don&apos;t prevent verification, but they will be recorded in the audit log.
          Review each item before continuing.
        </p>

        <ul className="mt-4 space-y-2">
          {warnings.map((w, i) => (
            <li
              key={i}
              className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-small"
            >
              <div className="text-caption font-semibold uppercase tracking-wide text-amber-700">
                {kindLabel(w.kind)}
              </div>
              <div className="mt-0.5 text-ink">{w.detail}</div>
            </li>
          ))}
        </ul>

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={busy}>
            {busy ? 'Verifying…' : 'Verify anyway'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
